import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, X, Loader2, FileText, Image as ImageIcon, Download } from "lucide-react";
import { toast } from "sonner";

const bytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export function AttachmentChip({ fileId, onRemove }) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get(`/files/${fileId}/meta`).then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, [fileId]);

  if (!meta) return null;
  const token = localStorage.getItem("kippu_token");
  const url = `${API}/files/${fileId}/download?auth=${token}`;
  const isImg = meta.content_type?.startsWith("image/");

  return (
    <div
      className="inline-flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors"
      data-testid={`attachment-chip-${fileId}`}
    >
      {isImg ? <ImageIcon className="w-3.5 h-3.5 text-indigo-500" /> : <FileText className="w-3.5 h-3.5 text-slate-500" />}
      <a href={url} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-indigo-600 max-w-[180px] truncate">
        {meta.original_filename}
      </a>
      <span className="text-slate-400">·</span>
      <span className="text-slate-400">{bytes(meta.size)}</span>
      <a href={url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600">
        <Download className="w-3 h-3" />
      </a>
      {onRemove && (
        <button onClick={() => onRemove(fileId)} className="text-slate-400 hover:text-rose-500 ml-0.5">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function AttachmentPicker({ value, onChange, compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newIds = [...(value || [])];
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.id);
      } catch (e) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    onChange(newIds);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeId = (id) => onChange((value || []).filter((x) => x !== id));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {(value || []).map((id) => (
          <AttachmentChip key={id} fileId={id} onRemove={removeId} />
        ))}
      </div>
      <div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="attachment-file-input"
        />
        <Button
          type="button"
          variant="outline"
          size={compact ? "sm" : "default"}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="attachment-add-btn"
          className="border-slate-200"
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Paperclip className="w-3.5 h-3.5 mr-1.5" />
          )}
          {uploading ? "Uploading..." : "Attach files"}
        </Button>
      </div>
    </div>
  );
}
